import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { getPayableAccounts, getReceivableAccounts } from 'lib/api';
import { getCompanyLogo } from 'lib/api/company';
import { getConciliationDocumentDetails } from 'lib/api/conciliations';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from 'lib/designSystem/HeaderWithActions';
import TableWithPagination from 'lib/designSystem/TableWithPagination';
import { LightTooltip } from 'lib/designSystem/WhiteTooltip';
import {
  hasAnyPermissions,
  rutChanger,
  showAlert,
  validaRut,
} from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import { generateAccountReportPDF } from '../../../Financing/common/exportAccountReport';
import DeleteDialog from './components/DeleteDialog';
import NewAccount from './components/NewAccount';

const headers: {
  key: string;
  label: string;
  type?: any;
  primaryLabel?: string;
  secondaryLabel?: string;
  textLabel?: string;
  dateLabel?: string;
  amountLabel?: string;
  sortKey?: string;
}[] = [
  { key: 'folio', label: 'Id', type: 'numeric-id' },
  {
    key: 'businessInfo',
    label: 'Acreedor',
    type: 'dual-text',
    primaryLabel: 'Acreedor',
    secondaryLabel: 'Rut Acreedor',
    sortKey: 'businessInfo.primary',
  },
  {
    key: 'stakeholderInfo',
    label: 'Deudor',
    type: 'dual-text',
    primaryLabel: 'Deudor',
    secondaryLabel: 'Rut Deudor',
    sortKey: 'stakeholderInfo.primary',
  },
  {
    key: 'categoryInfo',
    label: 'Categoría / Fecha',
    type: 'text-date',
    textLabel: 'Categoría',
    dateLabel: 'Fecha',
    sortKey: 'categoryInfo.date',
  },
  { key: 'extraData', label: 'Observación' },
  { key: 'amount', label: 'Monto', type: 'money' },
  {
    key: 'receivableInfo',
    label: 'Pago conciliado',
    type: 'payment-date',
    amountLabel: 'Monto Pago',
    dateLabel: 'Fecha Pago',
    sortKey: 'receivableInfo.date',
  },
  {
    key: 'paymentInfo',
    label: 'Monto utilizado',
    type: 'payment-date',
    amountLabel: 'Monto Pago',
    dateLabel: 'Fecha Pago',
    sortKey: 'paymentInfo.date',
  },
  {
    key: 'status',
    label: 'Estado de pago',
    type: 'status-chip',
  },
];

const exportHeaders: {
  key: string;
  label: string;
  type?: any;
  primaryLabel?: string;
  secondaryLabel?: string;
  textLabel?: string;
  dateLabel?: string;
  amountLabel?: string;
  sortKey?: string;
}[] = [
  ...headers,
  {
    key: 'executiveName',
    label: 'Ejecutivo',
  },
];

const Accounts = (props: any) => {
  const { businessIdentifier, profile } = props;
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const dispatch = useDispatch();
  const [payableAccounts, setPayableAccounts] = useState<any[]>([]);
  const [receivableAccounts, setReceivableAccounts] = useState<any[]>([]);
  const [payablePagination, setPayablePagination] = useState<any>({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });
  const [receivablePagination, setReceivablePagination] = useState<any>({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [accountType, setAccountType] = useState('Pagar');
  const [searchRut, setSearchRut] = useState(businessIdentifier || '');
  const [searchPaid, setSearchPaid] = useState('false');
  const [searchCategory, setSearchCategory] = useState('all');
  const [searchIdentifier, setSearchIdentifier] = useState('');

  const [selectedAccount, setSelectedAccount] = useState<any>(null);

  const newAccount = {
    id: '',
    category: '',
    observation: '',
    type: '',
    createdAt: null,
    amount: 0,
  };
  const [account, setAccount] = useState(newAccount);

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  const handleRequestQuote = async (id: string) => {
    try {
      setLoading(true);

      // Encontrar la cuenta en los datos actuales
      const accounts =
        accountType === 'Pagar' ? payableAccounts : receivableAccounts;
      const account = accounts.find((acc: any) => acc.id === id);

      if (!account) {
        showAlert(dispatch, 'error', 'Cuenta no encontrada');
        return;
      }
      let accountWithConciliation = { ...account };

      const documentId = account.documentId;

      if (documentId) {
        try {
          const conciliationData = await getConciliationDocumentDetails(
            dispatch,
            documentId,
          );
          if (conciliationData) {
            accountWithConciliation = {
              ...account,
              conciliationDetails: conciliationData.conciliationDetails,
              conciliations: conciliationData.conciliations,
            };
          }
        } catch (error) {
          console.error(
            'No se pudieron obtener los detalles de conciliación:',
            error,
          );
        }
      }

      const imageBase64 = await getCompanyLogo(dispatch);

      await generateAccountReportPDF(
        accountWithConciliation,
        accountType as 'Pagar' | 'Cobrar',
        user?.name ?? 'Usuario',
        imageBase64,
      );

      showAlert(dispatch, 'success', 'PDF generado exitosamente');
    } catch (error) {
      console.error('Error generando PDF:', error);
      showAlert(dispatch, 'error', 'Error al generar el PDF');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestQuoteObject = {
    key: 'requestQuote',
    label: '',
    handleAction: handleRequestQuote,
    icon: (
      <LightTooltip
        title={<Typography padding={2}>Solicitar cotización</Typography>}
      >
        <PictureAsPdfIcon />
      </LightTooltip>
    ),
  };

  const createQuery = ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    let query = `page=${page}&pageSize=${pageSize}`;
    if (searchRut) query += `&identifier=${searchRut}`;
    if (searchPaid !== 'all') query += `&paid=${searchPaid}`;
    if (searchCategory !== 'all') query += `&category=${searchCategory}`;
    if (searchIdentifier) query += `&searchIdentifier=${searchIdentifier}`;
    return query;
  };

  const checkRut = () => {
    if (searchRut != '' && !validaRut(searchRut)) {
      showAlert(dispatch, 'error', 'Rut inválido');
      return false;
    }
    return true;
  };

  const fetchPendingAccounts = async () => {
    await searchData({ page: 1, pageSize: 10, updateBothTypes: true });
  };

  const searchData = async ({
    page,
    pageSize,
    updateBothTypes = false,
  }: {
    page?: number;
    pageSize?: number;
    updateBothTypes?: boolean;
  }) => {
    setLoading(true);
    try {
      if (!checkRut()) return setLoading(false);

      const transformData = (data: any[]) =>
        data.map((item: any) => ({
          ...item,
          businessInfo: {
            primary: item.businessName || '',
            secondary: item.businessIdentifier || '',
          },
          stakeholderInfo: {
            primary: item.stakeholderName || '',
            secondary: item.stakeholderIdentifier || '',
          },
          categoryInfo: {
            text: item.category || '',
            date: item.issuedDate ? item.issuedDate : '',
          },
          receivableInfo: {
            amount: item.amountConciliated,
            date: item.lastConciliationPayment,
          },
          paymentInfo: {
            amount: item.amountConciliated,
            date: item.lastConciliationPayment,
          },
          status: (() => {
            if (item.paidAt) {
              return 'Pagado';
            } else if (item.amountConciliated === 0) {
              return 'Pendiente';
            } else if (
              item.amountConciliated > 0 &&
              item.amountConciliated < item.amount
            ) {
              return 'Parcial';
            } else if (item.amountConciliated >= item.amount) {
              return 'Pagado';
            } else {
              return 'Pendiente';
            }
          })(),
          paidAt: item.paidAt || null,
        }));

      if (updateBothTypes) {
        const [payableData, receivableData] = await Promise.all([
          getPayableAccounts(
            dispatch,
            createQuery({ page: page || 1, pageSize: pageSize || 10 }),
          ),
          getReceivableAccounts(
            dispatch,
            createQuery({ page: page || 1, pageSize: pageSize || 10 }),
          ),
        ]);

        setPayableAccounts(transformData(payableData.data));
        setReceivableAccounts(transformData(receivableData.data));
        setPayablePagination(payableData.pagination);
        setReceivablePagination(receivableData.pagination);

        setLoading(false);
        return { payableData, receivableData };
      } else {
        const data =
          accountType === 'Pagar'
            ? await getPayableAccounts(
                dispatch,
                createQuery({ page: page || 1, pageSize: pageSize || 10 }),
              )
            : await getReceivableAccounts(
                dispatch,
                createQuery({ page: page || 1, pageSize: pageSize || 10 }),
              );

        const transformedData = transformData(data.data);

        (accountType === 'Pagar' ? setPayableAccounts : setReceivableAccounts)(
          transformedData,
        );
        (accountType === 'Pagar'
          ? setPayablePagination
          : setReceivablePagination)(data.pagination);
        setLoading(false);
        return { ...data, data: transformedData };
      }
    } catch (error) {
      alert('Error al cargar las cuentas pendientes');
    }
    setLoading(false);
  };

  useEffect(() => {
    void fetchPendingAccounts();
  }, []);

  useEffect(() => {
    const canDelete = hasAnyPermissions(user, [
      'financing_pending_accounts_delete',
    ]);
    setCanDelete(canDelete);
  }, [user]);

  const handleOpen = () => {
    setOpen(true);
  };

  const payableCategories = [
    { label: 'Todos', value: 'all' },
    { label: 'Excedentes', value: 'excedentes' },
    { label: 'Factura no cedida', value: 'factura_no_cedida' },
    { label: 'General', value: 'cuenta_por_pagar_general' },
    { label: 'Intereses mora Fondo', value: 'cuenta_por_pagar_interes_fondo' },
    { label: 'Monto en garantía', value: 'monto_en_garantia' },
    {
      label: 'Recaudación por pagar fondo',
      value: 'recaudacion_por_pagar_fondo',
    },
    { label: 'Recompra facturas', value: 'cuenta_por_pagar_recompra_facturas' },
    { label: 'Confirming tradicional', value: 'confirming_tradicional' },
  ];
  const receivableCategories = [
    { label: 'Todos', value: 'all' },
    { label: 'Gasto notarial', value: 'gasto_notarial' },
    { label: 'Otros ingresos', value: 'otros_ingresos' },
    { label: 'Confirming tradicional', value: 'confirming_tradicional' },
    { label: 'Prórroga', value: 'prorroga' },
  ];

  const filters: Filter[] = [
    {
      type: 'select',
      label: 'Estado',
      value: searchPaid,
      setter: setSearchPaid,
      options: [
        { label: 'Todos', value: 'all' },
        { label: 'Pagado', value: 'true' },
        { label: 'No Pagado', value: 'false' },
      ],
    },
    {
      type: 'select',
      label: 'Categoría',
      value: searchCategory,
      setter: setSearchCategory,
      options:
        accountType === 'Pagar' ? payableCategories : receivableCategories,
    },
    {
      type: 'text',
      label: 'Id',
      value: searchIdentifier,
      setter: setSearchIdentifier,
    },
  ];

  const customSetter = (value: string) => {
    rutChanger(value, setSearchRut);
  };

  if (!profile) {
    filters.push({
      type: 'text',
      label: accountType === 'Pagar' ? 'Rut Acreedor' : 'Rut Deudor',
      value: searchRut,
      setter: customSetter,
    });
  }

  const buttons: CustomButtonProps[] = [
    {
      name: 'Buscar',
      color: 'primary',
      action: () => {},
      customButton: (
        <GlobalSearcher
          filters={filters}
          triggerSearch={() =>
            searchData({ page: 1, pageSize: 10, updateBothTypes: true })
          }
        />
      ),
    },
  ];

  if (!profile) {
    buttons.push({
      name: 'Nueva Cuenta',
      action: handleOpen,
      color: 'primary',
      disabled: !hasAnyPermissions(user, ['financing_pending_accounts_new']),
    });
  }

  const handleOpenDeleteDialog = (row: any) => {
    const accounts =
      accountType === 'Pagar' ? payableAccounts : receivableAccounts;
    const account = accounts.find((x: any) => x.id === row);
    setSelectedAccount(account);
    setOpenDeleteDialog(true);
  };

  const actions = [
    {
      name: 'Eliminar',
      action: handleOpenDeleteDialog,
      actionLabel: 'Eliminar',
      disabled: !canDelete,
    },
  ];

  const handleChangeType = (newValue: 'Pagar' | 'Cobrar' | null) => {
    if (newValue) {
      setAccountType(newValue);
      setSearchRut('');
      setSearchPaid('false');
      setSearchCategory('all');
      setSearchIdentifier('');
    }
  };

  if (loading) return <CenteredCircularProgress relative />;

  const receivableHeaders = headers.filter(
    (x: any) => !['businessInfo', 'paymentInfo'].includes(x.key),
  );
  const payableHeaders = headers.filter(
    (x: any) => !['stakeholderInfo', 'receivableInfo'].includes(x.key),
  );

  return (
    <div>
      <HeaderWithActions
        buttons={buttons}
        variant="body1"
        customTitle={
          <Box>
            <ToggleButtonGroup
              value={accountType}
              color="primary"
              exclusive
              onChange={(_, newValue) => {
                handleChangeType(newValue);
              }}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  minWidth: '140px',
                },
              }}
            >
              <ToggleButton value="Pagar">Por pagar</ToggleButton>
              <ToggleButton value="Cobrar">Por cobrar</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        }
      />
      <TableWithPagination
        exportToFile="xlsx"
        data={accountType === 'Pagar' ? payableAccounts : receivableAccounts}
        setData={
          accountType === 'Pagar' ? setPayableAccounts : setReceivableAccounts
        }
        searchData={searchData}
        pagination={
          accountType === 'Pagar' ? payablePagination : receivablePagination
        }
        setPagination={
          accountType === 'Pagar'
            ? setPayablePagination
            : setReceivablePagination
        }
        dataHeaders={[
          handleRequestQuoteObject,
          ...(accountType === 'Pagar' ? payableHeaders : receivableHeaders),
        ]}
        exportHeaders={exportHeaders}
        actions={actions}
        loading={loading}
        finalSumRow
        lastElement
      />
      {open && (
        <NewAccount
          open={open}
          setOpen={setOpen}
          account={account}
          setAccount={setAccount}
          setUp={fetchPendingAccounts}
        />
      )}
      {openDeleteDialog && canDelete && (
        <DeleteDialog
          open={openDeleteDialog}
          onClose={() => {
            setSelectedAccount(null);
            setOpenDeleteDialog(false);
          }}
          searchData={(params: { page?: number; pageSize?: number }) =>
            searchData({ ...params, updateBothTypes: true })
          }
          accountType={accountType}
          account={selectedAccount}
        />
      )}
    </div>
  );
};

export default Accounts;
